import { ChangeEvent, FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

type Block = {
  hash: string;
  prevHash: string;
  height: number;
  version: number | string;
  data: string;
  timestamp: number | string;
  scope: string;
  by: string;
  signature: string;
  size?: number;
  prettyDate?: string;
  body?: string;
  signature_verified?: boolean;
  hash_verified?: boolean;
  result?: string;
  error?: string;
  __err?: string;
  __logs?: unknown;
  __storage?: Record<string, unknown>;
  encripted?: boolean;
  formattedDecryptedData?: string;
  htmlDescription?: string;
};

type WebSocketPayload = {
  clientId?: string;
  topic?: string;
  value?: Block;
  type?: string;
  message?: string;
};

type HeadResponse = {
  head: string;
  height: number;
  progress?: string;
};

type AceSession = {
  setMode: (mode: string) => void;
  setUseWrapMode: (useWrap: boolean) => void;
};

type AceRenderer = {
  updateFull?: () => void;
};

type AceEditor = {
  getSession: () => AceSession;
  setTheme: (theme: string) => void;
  setReadOnly: (readOnly: boolean) => void;
  setShowPrintMargin: (show: boolean) => void;
  setHighlightActiveLine: (highlight: boolean) => void;
  setValue: (value: string, cursorPosition?: number) => void;
  destroy: () => void;
  renderer?: AceRenderer;
};

type Ace = {
  edit: (element: HTMLElement) => AceEditor;
  require?: (module: string) => void;
};

declare global {
  interface Window {
    ace?: Ace;
  }
}

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const USE_TESTNET_NODE = true;
const TESTNET_NODE_URL = "https://testnet.democrachain.org/v1";
const BASE_PATH = "/testnet";
const WEBSOCKET_URL = "wss://websockets.democrachain.org/connect/testnet";
const WEBSOCKET_TOPIC_PREFIX = "newblock:";
const WEBSOCKET_SUBSCRIPTION = `${WEBSOCKET_TOPIC_PREFIX}*`;
const WEBSOCKET_RECONNECT_BASE_MS = 1500;
const WEBSOCKET_RECONNECT_MAX_MS = 15000;
const SCOPE_STORAGE_KEY = "democrachain:scope";
const PUBKEY_STORAGE_PREFIX = "democrachain:pubkey:";
const BLOCKS_AROUND_COUNT = 5;
const BLOCKS_AROUND_TOTAL = BLOCKS_AROUND_COUNT * 2 + 1;
const DEFAULT_BLOCKS_PER_PAGE = 25;

function normalizeScope(value?: string | null) {
  if (!value) {
    return "";
  }
  return value.trim().toLowerCase();
}

function getInitialScope() {
  if (typeof window === "undefined") {
    return "root";
  }
  const urlScope = new URLSearchParams(window.location.search).get("scope");
  if (urlScope) {
    return urlScope;
  }
  return window.localStorage.getItem(SCOPE_STORAGE_KEY) || "root";
}

const LOGO_SRC = "https://github.com/democrachain.png";

function getNodeURL() {
  if (typeof window === "undefined") {
    return "";
  }
  const { hostname, origin } = window.location;
  // if (LOCAL_HOSTS.has(hostname)) {
  //   return `http://${hostname}:5300/development/v1`;
  // }
  if (USE_TESTNET_NODE) {
    return TESTNET_NODE_URL;
  }
  return `${origin}/v1`;
}

function getWebSocketURL() {
  if (typeof window === "undefined") {
    return WEBSOCKET_URL;
  }
  if (LOCAL_HOSTS.has(window.location.hostname)) {
    return "ws://localhost:8787/connect/testnet";
  }
  return WEBSOCKET_URL;
}

function getCachedPublicKey(who: string) {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(`${PUBKEY_STORAGE_PREFIX}${who}`);
  } catch (error) {
    return null;
  }
}

function setCachedPublicKey(who: string, value: string) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(`${PUBKEY_STORAGE_PREFIX}${who}`, value);
  } catch (error) {
    // Ignore storage write issues (private mode, quota).
  }
}

function clearCachedPublicKey(who: string) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(`${PUBKEY_STORAGE_PREFIX}${who}`);
  } catch (error) {
    // Ignore storage remove issues.
  }
}

type PathParams = {
  hash: string | null;
};

function getPathParams(): PathParams {
  if (typeof window === "undefined") {
    return { hash: null as string | null };
  }

  const normalized = window.location.pathname.replace(/\/+$/, "");
  const hashMatch = normalized.match(/^\/testnet\/block\/hash\/([^/]+)$/i);
  if (hashMatch) {
    return { hash: decodeURIComponent(hashMatch[1]) };
  }

  return { hash: null as string | null };
}

let aceLoader: Promise<Ace | null> | null = null;

function loadAce(): Promise<Ace | null> {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }
  if (window.ace) {
    return Promise.resolve(window.ace);
  }
  if (aceLoader) {
    return aceLoader;
  }
  aceLoader = new Promise<Ace | null>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/ace/1.12.5/ace.js";
    script.async = true;
    script.onload = () => resolve(window.ace ?? null);
    script.onerror = () => reject(new Error("Failed to load Ace editor."));
    document.body.appendChild(script);
  }).catch(() => null);
  return aceLoader;
}

function buf2hex(buffer: ArrayBuffer) {
  return Array.from(new Uint8Array(buffer))
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");
}

function getPreviewText(data?: string) {
  if (!data) {
    return "-";
  }
  const collapsed = data.replace(/\s+/g, " ").trim();
  if (!collapsed) {
    return "-";
  }
  return collapsed.length > 80 ? `${collapsed.slice(0, 80)}...` : collapsed;
}

type MiddleEllipsisProps = {
  text: string;
  className?: string;
  title?: string;
};

function getMiddleEllipsisText(text: string, maxWidth: number, measure: (value: string) => number) {
  const ellipsis = "...";
  if (!text) {
    return text;
  }
  if (measure(text) <= maxWidth) {
    return text;
  }
  if (measure(ellipsis) >= maxWidth) {
    return ellipsis;
  }

  let low = 0;
  let high = text.length;
  let best = ellipsis;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const head = Math.ceil(mid / 2);
    const tail = Math.floor(mid / 2);
    const candidate = `${text.slice(0, head)}${ellipsis}${text.slice(text.length - tail)}`;
    if (measure(candidate) <= maxWidth) {
      best = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best;
}

function MiddleEllipsis({ text, className, title }: MiddleEllipsisProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [displayText, setDisplayText] = useState(text);

  const update = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const el = ref.current;
    if (!el) {
      return;
    }
    const maxWidth = el.parentElement?.clientWidth ?? el.clientWidth;
    if (!maxWidth) {
      setDisplayText(text);
      return;
    }
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) {
      setDisplayText(text);
      return;
    }
    const style = window.getComputedStyle(el);
    const font =
      style.font ||
      `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} / ${style.lineHeight} ${style.fontFamily}`;
    ctx.font = font;
    const next = getMiddleEllipsisText(text, maxWidth, (value) => ctx.measureText(value).width);
    setDisplayText((prev) => (prev === next ? prev : next));
  }, [text]);

  useLayoutEffect(() => {
    update();
  }, [update]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    if (el.parentElement) {
      observer.observe(el.parentElement);
    }
    return () => observer.disconnect();
  }, [update]);

  return (
    <span ref={ref} className={`middle-ellipsis${className ? ` ${className}` : ""}`} title={title ?? text}>
      {displayText}
    </span>
  );
}

const HASH_LIKE_RE = /^[a-f0-9]{64}$/i;

function isHashLike(value: string) {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  return HASH_LIKE_RE.test(trimmed);
}

type HashShortProps = {
  value: string;
  className?: string;
  maxLength?: number;
};

function HashShort({ value, className, maxLength }: HashShortProps) {
  const displayValue = maxLength && value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
  return (
    <span className={`hash-short mono-font${className ? ` ${className}` : ""}`} title={value}>
      {displayValue}
    </span>
  );
}

type HashAwareTextProps = {
  value: string;
  className?: string;
  useMiddleEllipsis?: boolean;
};

function HashAwareText({ value, className, useMiddleEllipsis = false }: HashAwareTextProps) {
  const trimmed = value.trim();
  const normalizedValue = trimmed || value;
  if (isHashLike(normalizedValue)) {
    return <HashShort value={normalizedValue} className={className} />;
  }
  if (useMiddleEllipsis && normalizedValue.length === 64) {
    return <MiddleEllipsis text={normalizedValue} className={className} />;
  }
  return (
    <span className={className} title={value}>
      {value}
    </span>
  );
}

type ScopeLabelProps = {
  value: string;
};

function ScopeLabel({ value }: ScopeLabelProps) {
  if (isHashLike(value)) {
    return <HashShort value={value} />;
  }
  return (
    <span className="scope-label" title={value}>
      {value}
    </span>
  );
}

function getImportHash(data?: string) {
  if (!data) {
    return null;
  }
  const trimmed = data.trimStart();
  if (!trimmed.startsWith("//")) {
    return null;
  }
  const firstLine = trimmed.split("\n")[0] || "";
  const match = firstLine.match(/^\/\/\s*IMPORT\s+([a-fA-F0-9]+)\b/);
  return match ? match[1] : null;
}

function normalizeStorageKey(key: string) {
  const withoutPrefix = key.replace(/^\/_storage\/__[^/]+\//, "");
  return withoutPrefix.replace(/^\/_storage\//, "").replace(/\.json$/i, "");
}

function getStorageLink(scope: string, key: string) {
  if (!key) {
    return "";
  }
  const trimmedKey = key.trim();
  if (!trimmedKey) {
    return "";
  }
  const normalizedKey = normalizeStorageKey(trimmedKey).trim();
  if (!normalizedKey) {
    return "";
  }
  const normalizedKeyPath = normalizedKey.replace(/^\/+/, "");
  if (!normalizedKeyPath) {
    return "";
  }
  if (normalizedKeyPath.startsWith("_")) {
    return `${TESTNET_NODE_URL}/${normalizedKeyPath}`;
  }
  const normalizedScope = scope.trim();
  if (!normalizedScope) {
    return "";
  }
  return `${TESTNET_NODE_URL}/${normalizedScope}/_/${normalizedKeyPath}`;
}

function formatStorageValue(value: unknown) {
  if (value === null) {
    return "null";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
}

function formatLogArg(value: unknown) {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "undefined";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function formatTableFromArgs(args: unknown[]) {
  let rows: Record<string, unknown>[] = [];
  if (args.length === 1 && Array.isArray(args[0])) {
    const arrayArg = args[0];
    if (arrayArg.every((item) => isPlainObject(item))) {
      rows = arrayArg as Record<string, unknown>[];
    }
  } else if (args.length && args.every((item) => isPlainObject(item))) {
    rows = args as Record<string, unknown>[];
  }

  if (!rows.length) {
    return "";
  }

  const columns: string[] = [];
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
  });

  if (!columns.length) {
    return "";
  }

  const cellRows = rows.map((row) =>
    columns.map((col) => {
      const value = row[col];
      return value === undefined ? "" : formatLogArg(value);
    }),
  );
  const widths = columns.map((col, index) => {
    const cellWidth = Math.max(0, ...cellRows.map((row) => row[index].length));
    return Math.max(col.length, cellWidth);
  });
  const pad = (value: string, width: number) => value.padEnd(width, " ");
  const top = `┌${widths.map((width) => "─".repeat(width + 2)).join("┬")}┐`;
  const header = `│ ${columns.map((col, index) => `${pad(col, widths[index])} `).join("│ ")}│`;
  const separator = `├${widths.map((width) => "─".repeat(width + 2)).join("┼")}┤`;
  const body = cellRows.map(
    (row) => `│ ${row.map((cell, index) => `${pad(cell, widths[index])} `).join("│ ")}│`,
  );
  const bottom = `└${widths.map((width) => "─".repeat(width + 2)).join("┴")}┘`;

  return [top, header, separator, ...body, bottom].join("\n");
}

type LogEntry = {
  level: "log" | "error" | "table" | "warn" | "info";
  text: string;
};

function normalizeLogEntries(logs: unknown): LogEntry[] {
  if (!logs) {
    return [];
  }

  const buildEntry = (level: unknown, args: unknown): LogEntry => {
    let normalizedLevel: LogEntry["level"] = "log";
    if (typeof level === "string") {
      const lowerLevel = level.toLowerCase();
      if (lowerLevel === "error") {
        normalizedLevel = "error";
      } else if (lowerLevel === "table") {
        normalizedLevel = "table";
      } else if (lowerLevel === "warn") {
        normalizedLevel = "warn";
      } else if (lowerLevel === "info") {
        normalizedLevel = "info";
      }
    }
    const argArray = Array.isArray(args) ? args : args === undefined ? [] : [args];
    if (normalizedLevel === "table") {
      const table = formatTableFromArgs(argArray);
      const text = table || argArray.map(formatLogArg).join(" ");
      return { level: "table", text };
    }
    return { level: normalizedLevel, text: argArray.map(formatLogArg).join(" ") };
  };

  if (typeof logs === "string") {
    return [{ level: "log", text: logs }];
  }

  if (Array.isArray(logs)) {
    return logs.flatMap((entry) => {
      if (typeof entry === "string") {
        return [{ level: "log", text: entry }];
      }
      if (Array.isArray(entry)) {
        return [{ level: "log", text: entry.map(formatLogArg).join(" ") }];
      }
      if (isPlainObject(entry)) {
        if ("level" in entry || "args" in entry) {
          return [buildEntry(entry.level, entry.args)];
        }
        return [{ level: "log", text: formatLogArg(entry) }];
      }
      return [{ level: "log", text: formatLogArg(entry) }];
    });
  }

  if (isPlainObject(logs) && ("level" in logs || "args" in logs)) {
    return [buildEntry(logs.level, logs.args)];
  }

  return [{ level: "log", text: formatLogArg(logs) }];
}

function removeLines(str: string) {
  return str.replace("\n", "");
}

function base64ToArrayBuffer(b64: string) {
  const byteString = window.atob(b64);
  const byteArray = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    byteArray[i] = byteString.charCodeAt(i);
  }
  return byteArray;
}

function pemToArrayBufferPublic(pem: string) {
  const b64Lines = removeLines(pem);
  const b64Prefix = b64Lines.replace("-----BEGIN PUBLIC KEY-----", "");
  const b64Final = b64Prefix.replace("-----END PUBLIC KEY-----", "");
  return base64ToArrayBuffer(b64Final);
}

async function decryptText(data: Uint8Array<ArrayBuffer>, password: string) {
  const pwUtf8 = new TextEncoder().encode(password);
  const pwHash = await window.crypto.subtle.digest("SHA-256", pwUtf8);

  const iv = new Uint8Array(16);
  const alg = { name: "AES-CBC", iv };
  const key = await window.crypto.subtle.importKey("raw", pwHash, alg, false, ["decrypt"]);
  const result = await window.crypto.subtle.decrypt(alg, key, data);
  return new TextDecoder().decode(result);
}

async function decryptTextFromBase64(data: string, password: string) {
  return decryptText(base64ToArrayBuffer(data), password);
}

async function decryptBlockData(data: string) {
  if (!data) {
    return { encrypted: false, formatted: "" };
  }

  const lines = data.split("\n");
  let encrypted = false;
  const decryptedLines: string[] = [];

  for (const line of lines) {
    const words = line.split(" ");
    for (let i = 0; i < words.length; i += 1) {
      const word = words[i];
      if (word.startsWith("::")) {
        encrypted = true;
        try {
          words[i] = await decryptTextFromBase64(word.replace("::", ""), "123");
        } catch (error) {
          words[i] = word;
        }
      }
    }
    decryptedLines.push(words.join(" "));
  }

  return {
    encrypted,
    formatted: decryptedLines.join("\n"),
  };
}

function Logo() {
  return (
    <span className="logo-link">
      <img className="brand-logo" src={LOGO_SRC} alt="Democrachain logo" />
    </span>
  );
}

function BlockDataViewer({ data }: { data: string }) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const aceRef = useRef<AceEditor | null>(null);
  const editorHeight = 20 * 22 + 32; // 45 lines * 22px per line + padding

  useEffect(() => {
    let cancelled = false;

    async function setupEditor() {
      const ace = await loadAce();
      if (!ace || cancelled || !editorRef.current) {
        return;
      }
      if (!aceRef.current) {
        ace.require?.("ace/ext/language_tools");
        const editor = ace.edit(editorRef.current);
        editor.getSession().setMode("ace/mode/javascript");
        editor.getSession().setUseWrapMode(true);
        editor.setTheme("ace/theme/merbivore");
        editor.setReadOnly(true);
        editor.setShowPrintMargin(false);
        editor.setHighlightActiveLine(false);
        aceRef.current = editor;
      }
      aceRef.current.setValue(data, -1);
      aceRef.current.renderer?.updateFull?.();
    }

    void setupEditor();

    return () => {
      cancelled = true;
    };
  }, [data]);

  useEffect(() => {
    return () => {
      if (aceRef.current) {
        aceRef.current.destroy();
        aceRef.current = null;
      }
    };
  }, []);

  return <div ref={editorRef} className="block-editor" style={{ height: `${editorHeight}px` }} />;
}

type BlockDetailsProps = {
  block: Block;
  nodeURL: string;
  isScopeHash: boolean;
  onOpenScopeChain?: (scope: string) => void;
  showClose?: boolean;
  onClose?: () => void;
  onHashClick?: (hash: string) => void;
  nextBlockHash?: string;
  previousBlockHash?: string;
};

function BlockDetails({
  block,
  nodeURL,
  isScopeHash,
  onOpenScopeChain,
  showClose,
  onClose,
  onHashClick,
  nextBlockHash,
  previousBlockHash,
}: BlockDetailsProps) {
  const [hashCopied, setHashCopied] = useState(false);
  const hashStatus = block.hash_verified === undefined ? "" : block.hash_verified ? "🟢" : "🔴";
  const signatureStatus =
    block.signature_verified === undefined ? "" : block.signature_verified ? "🟢" : "🔴";
  const signatureHtml = block.signature ? block.signature.split("\n").join("<br />") : "";
  const blockData = block.data || "";
  const importHash = getImportHash(block.data);
  const isScopeImport = Boolean(
    importHash && block.scope && importHash.toLowerCase() === block.scope.toLowerCase(),
  );
  const startsWithSlashes = blockData.trimStart().startsWith("//");
  const decryptedBody = block.formattedDecryptedData ?? "";
  const descriptionBody = block.htmlDescription ?? "";
  const logEntries = useMemo(() => normalizeLogEntries(block.__logs), [block.__logs]);
  const hasLogs = logEntries.length > 0;
  const canFollowHash = Boolean(onHashClick && block.hash);
  const storageEntries = useMemo(() => {
    const storage = block.__storage;
    if (!storage || typeof storage !== "object") {
      return [];
    }
    return Object.entries(storage)
      .map(([key, value]) => ({
        key: normalizeStorageKey(key),
        rawKey: key.trim(),
        value,
      }))
      .filter((entry) => Boolean(entry.key));
  }, [block.__storage]);

  return (
    <div className="panel border-gradient block-details">
      <div className="block-details-inner">
        <div className="block-details-header">
          <div className="block-title-area">
            <div className="block-info-text mono-font">
              <div>
                Block <span className="block-hash-emphasis" title={block.hash}>{block.hash}</span>
              </div>
              <div>
                Number {block.height} of scope <span className="scope-emphasis"><ScopeLabel value={block.scope} /></span>
              </div>
              <div>
                Signed by {block.by} ({block.prettyDate})
              </div>
            </div>

          </div>
          {showClose ? (
            <button
              type="button"
              className="btn-close clickable block-details-close"
              onClick={onClose}
              aria-label="Close"
            />
          ) : null}
        </div>

        <div className="block-info-grid">
          {isScopeHash ? (
            <div className="block-info-card">
              <div className="block-info-icon">📜</div>
              <div className="block-info-content">
                <div className="block-info-title">Smart Contract Definition</div>
                <div className="block-info-description">
                  This block is a smart contract that can be imported and executed by other blocks using <div><code>// IMPORT {block.hash.substring(0, 8)}...</code></div>
                </div>
                {onOpenScopeChain ? (
                  <button
                    type="button"
                    className="button-secondary button-compact"
                    onClick={() => onOpenScopeChain(block.hash)}
                  >
                    View blocks using this contract
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {startsWithSlashes ? (
            <div className="block-info-card">
              <div className="block-info-icon">⚙️</div>
              <div className="block-info-content">
                <div className="block-info-title">Smart Contract Executed</div>
                <div className="block-info-description">
                  This block executes a smart contract{importHash ? ' imported from another block' : ''}.
                </div>
                {importHash && onHashClick ? (
                  <button
                    type="button"
                    className="button-secondary button-compact button-gold"
                    onClick={() => onHashClick(importHash)}
                  >
                    View contract definition
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {!startsWithSlashes ? (
            <div className="block-info-card">
              <div className="block-info-icon">📦</div>
              <div className="block-info-content">
                <div className="block-info-title">Data Block</div>
                <div className="block-info-description">
                  This block contains raw data without smart contract execution.
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="block-data-section">
          <div className="block-section-label mono-font">CONTENT:</div>
          {startsWithSlashes ? (
            <BlockDataViewer data={blockData} />
          ) : (
            <pre className="block-data-plain mono-font">{blockData}</pre>
          )}

          {block.result ? (
            <div className="block-message-box block-result-box">
              <div className="block-message-header">
                <strong>✅ Smart contract result</strong>
              </div>
              <div className="block-message-content">
                {block.result}
              </div>
            </div>
          ) : null}

          {block.error || block.__err ? (
            <div className="block-message-box block-error-box">
              <div className="block-message-header">
                <strong>🚨 {block.__err ? 'Error' : 'Smart contract error'}</strong>
              </div>
              <div className="block-message-content">
                {block.error || block.__err}
              </div>
            </div>
          ) : null}
        </div>

        {hasLogs ? (
          <div className="block-logs-panel">
            <div className="block-section-label mono-font">LOGS:</div>
            <div className="block-logs mono-font">
              {logEntries.map((entry, index) => (
                <div
                  key={`${entry.level}-${index}`}
                  className={`block-log-entry${entry.level === "error" ? " block-log-entry--error" : ""}${entry.level === "warn" ? " block-log-entry--warn" : ""}${entry.level === "info" ? " block-log-entry--info" : ""}${entry.level === "table" ? " block-log-entry--table" : ""}`}
                >
                  {entry.text}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {storageEntries.length && !block.error && !block.__err ? (
          <div className="output-panel">
            <div className="block-section-label mono-font">OUTPUT:</div>
            <div className="output-list mono-font">
              {storageEntries.map((entry) => {
                const storageLink = getStorageLink(block.scope, entry.rawKey);
                return (
                  <div key={entry.key} className="output-item">
                    {storageLink ? (
                      <a
                        className="output-key output-link"
                        href={storageLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {entry.key}
                      </a>
                    ) : (
                      <span className="output-key">{entry.key}</span>
                    )}
                    <span className="output-value">{formatStorageValue(entry.value)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="block-verification-grid">
          <div className="block-verification-item">
            <div className="signature-hover-area" title={`Signature:\n${block.signature}`}>
              <div className="signature-status">
                {signatureStatus} Signature verified with {block.by.toUpperCase()}'s public key.
              </div>
              <div className="text-right"> <a
                className="button-secondary button-compact "
                href={`https://testnet.democrachain.org/v1/_keys/${block.by}.pub.key`}
                target="_blank"
                rel="noreferrer"
              >
                View Public Key
              </a></div>

            </div>
          </div>

          <div className="block-verification-item">
            <div className="signature-hover-area" title={`Hash: ${block.hash}`}>
              <div className="signature-status">{hashStatus} Hash verified, block integrity confirmed.</div>
              <div className="text-right"> <a
                className="button-secondary button-compact "
                href={`https://testnet.democrachain.org/v1/_block/${block.hash}`}
                target="_blank"
                rel="noreferrer"
              >
                View RAW block
              </a></div>
            </div>
          </div>
        </div>
      </div>

      <div className="accordion" id="accordionPrivate">
        {block.encripted ? (
          <div className="accordion-item">
            <h2 className="accordion-header" id="headingPrivado">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapsePrivado"
                aria-expanded="false"
                aria-controls="collapsePrivado"
              >
                <strong>PRIVATE DATA {signatureStatus} (decrypted)</strong>
              </button>
            </h2>
            <div
              id="collapsePrivado"
              className="accordion-collapse collapse"
              aria-labelledby="headingPrivado"
              data-bs-parent="#accordionPrivate"
            >
              <div className="accordion-body">
                <i>
                  <div className="text-break preformatted-content">{decryptedBody}</div>
                </i>
              </div>
            </div>
          </div>
        ) : null}
        {block.htmlDescription ? (
          <div className="accordion-item">
            <h2 className="accordion-header" id="headingDescription">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseDescription"
                aria-expanded="false"
                aria-controls="collapseDescription"
              >
                <strong>What does this block do?</strong>
              </button>
            </h2>
            <div
              id="collapseDescription"
              className="accordion-collapse collapse"
              aria-labelledby="headingDescription"
              data-bs-parent="#accordionPrivate"
            >
              <div className="accordion-body">
                <i>
                  <div className="text-break preformatted-content">{descriptionBody}</div>
                </i>
              </div>
            </div>
          </div>
        ) : null}

        <div className="block-details-bottom">
          <button
            type="button"
            className="button-secondary button-compact block-view-nav-button"
            onClick={previousBlockHash ? () => onHashClick?.(previousBlockHash) : undefined}
            disabled={!previousBlockHash}
          >
            Previous
          </button>
          <button
            type="button"
            className="button-secondary button-compact block-view-nav-button"
            onClick={nextBlockHash ? () => onHashClick?.(nextBlockHash) : undefined}
            disabled={!nextBlockHash}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const nodeURL = useMemo(() => getNodeURL(), []);
  const [pathParams, setPathParams] = useState<PathParams>(() => getPathParams());
  const [searchParams, setSearchParams] = useState(
    () => (typeof window === "undefined" ? new URLSearchParams() : new URLSearchParams(window.location.search)),
  );
  const [selectedScope, setSelectedScope] = useState(getInitialScope);
  const [scopeInput, setScopeInput] = useState(getInitialScope);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [scopesError, setScopesError] = useState<string | null>(null);
  const hashParam = pathParams.hash ?? searchParams.get("hash");
  const isBlockView = Boolean(hashParam);
  const scopeSet = useMemo(() => {
    const set = new Set<string>();
    availableScopes.forEach((scope) => {
      const normalized = normalizeScope(scope);
      if (normalized) {
        set.add(normalized);
      }
    });
    return set;
  }, [availableScopes]);

  const [blocks, setBlocks] = useState<Block[]>([]);
  const blocksPerHashRef = useRef<Record<string, Block>>({});
  const [freshBlocks, setFreshBlocks] = useState<Set<string>>(() => new Set());
  const [blocksPerPage] = useState<number>(DEFAULT_BLOCKS_PER_PAGE);
  const [pagesLoaded, setPagesLoaded] = useState<number>(1);
  const [hasMoreBlocks, setHasMoreBlocks] = useState<boolean>(true);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState<boolean>(false);
  const [isLoadingLatest, setIsLoadingLatest] = useState<boolean>(false);
  const [followHead, setFollowHead] = useState<boolean>(false);
  const [lastBlockHash, setLastBlockHash] = useState<string | null>(null);
  const [latestBlockHeight, setLatestBlockHeight] = useState<number | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>("🚀 Updating...");
  const [syncStatus, setSyncStatus] = useState<string | null>("Syncing...");
  const [blockView, setBlockView] = useState<Block | null>(null);
  const [blockViewError, setBlockViewError] = useState<string | null>(null);
  const blockDetailsRef = useRef<HTMLDivElement | null>(null);

  const currentLastBlockHash = useRef<string | null>(null);
  const publicKeysRef = useRef<Record<string, CryptoKey>>({});
  const pendingKeysRef = useRef<Partial<Record<string, Promise<CryptoKey | null>>>>({});
  const scopeKeyRef = useRef<CryptoKey | null>(null);
  const selectedScopeRef = useRef<string>(selectedScope);
  const heightToBlockRef = useRef<Record<number, Block>>({});
  const blockFetchPromisesRef = useRef<Partial<Record<number, Promise<Block | undefined>>>>({});
  const latestBlockHeightRef = useRef<number | null>(null);
  const nextHeightToLoadRef = useRef<number | null>(null);
  const isBlockViewRef = useRef<boolean>(isBlockView);
  const followHeadRef = useRef<boolean>(followHead);
  const websocketRef = useRef<WebSocket | null>(null);
  const websocketReconnectAttemptsRef = useRef<number>(0);

  const isUnknownScope = useMemo(() => {
    if (!availableScopes.length) {
      return false;
    }
    const normalized = normalizeScope(selectedScope);
    if (!normalized) {
      return false;
    }
    return !scopeSet.has(normalized);
  }, [availableScopes.length, scopeSet, selectedScope]);

  const activeScope = blockView?.scope ?? selectedScope;
  const playgroundUrl = "https://playground.democrachain.org/";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handlePopState = () => {
      setPathParams(getPathParams());
      setSearchParams(new URLSearchParams(window.location.search));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const urlScope = searchParams.get("scope");
    if (!urlScope) {
      return;
    }
    const trimmedScope = urlScope.trim();
    if (!trimmedScope) {
      return;
    }
    const normalizedUrlScope = normalizeScope(trimmedScope);
    const normalizedCurrentScope = normalizeScope(selectedScopeRef.current);
    if (normalizedUrlScope === normalizedCurrentScope) {
      if (trimmedScope !== selectedScopeRef.current) {
        setSelectedScope(trimmedScope);
        setScopeInput(trimmedScope);
      }
      return;
    }
    setSelectedScope(trimmedScope);
    setScopeInput(trimmedScope);
  }, [searchParams]);

  useEffect(() => {
    setScopeInput(selectedScope);
  }, [selectedScope]);

  useEffect(() => {
    selectedScopeRef.current = activeScope;
  }, [activeScope]);

  useEffect(() => {
    latestBlockHeightRef.current = latestBlockHeight;
  }, [latestBlockHeight]);

  useEffect(() => {
    isBlockViewRef.current = isBlockView;
  }, [isBlockView]);

  useEffect(() => {
    followHeadRef.current = followHead;
  }, [followHead]);


  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(SCOPE_STORAGE_KEY, selectedScope);
  }, [selectedScope]);

  useEffect(() => {
    let cancelled = false;

    async function loadScopes() {
      try {
        const response = await fetch(`${TESTNET_NODE_URL}/_head/`);
        const data = (await response.json()) as string[];
        if (cancelled) {
          return;
        }
        const normalized = Array.isArray(data) ? data.filter(Boolean) : [];
        const unique = new Set(normalized.map((scope) => String(scope)));
        unique.add("root");
        const sorted = Array.from(unique).sort((a, b) => {
          if (a === "root") return -1;
          if (b === "root") return 1;
          return a.localeCompare(b);
        });
        setAvailableScopes(sorted);
        setScopesError(null);
      } catch (error) {
        if (!cancelled) {
          setScopesError("Unable to load scopes.");
        }
      }
    }

    void loadScopes();

    return () => {
      cancelled = true;
    };
  }, []);

  const updatePath = useCallback((next: { hash?: string | null }) => {
    if (typeof window === "undefined") {
      return;
    }
    const url = new URL(window.location.href);
    if (next.hash) {
      url.pathname = `${BASE_PATH}/block/hash/${encodeURIComponent(next.hash)}`;
    } else {
      url.pathname = BASE_PATH;
    }
    url.searchParams.delete("hash");
    url.searchParams.delete("scope");
    window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
    setPathParams(getPathParams());
    setSearchParams(new URLSearchParams(window.location.search));
  }, []);

  useEffect(() => {
    const legacyHash = searchParams.get("hash");
    if (!pathParams.hash && legacyHash) {
      updatePath({ hash: legacyHash });
    }
  }, [pathParams.hash, searchParams, updatePath]);

  const updateScopeParam = useCallback((scope: string) => {
    if (typeof window === "undefined") {
      return;
    }
    const url = new URL(window.location.href);
    url.pathname = BASE_PATH;
    url.searchParams.set("scope", scope);
    url.searchParams.delete("hash");
    window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
    setPathParams(getPathParams());
    setSearchParams(new URLSearchParams(window.location.search));
  }, []);

  const resetBlockList = useCallback(() => {
    setBlocks([]);
    blocksPerHashRef.current = {};
    setFreshBlocks(new Set());
    setPagesLoaded(1);
    setHasMoreBlocks(true);
    setIsLoadingBlocks(false);
    setIsLoadingLatest(false);
    setFollowHead(false);
    setLastBlockHash(null);
    setLastUpdate("🚀 Updating...");
    currentLastBlockHash.current = null;
    nextHeightToLoadRef.current = null;
    heightToBlockRef.current = {};
    blockFetchPromisesRef.current = {};
    setLatestBlockHeight(null);
    latestBlockHeightRef.current = null;
  }, []);

  const listKey = `list:${activeScope}`;

  useEffect(() => {
    if (!isBlockView) {
      resetBlockList();
    }
  }, [listKey, blocksPerPage, isBlockView, resetBlockList]);

  const applyScope = useCallback(
    (nextScope: string) => {
      const normalized = nextScope.trim() || "root";
      setSelectedScope(normalized);
      resetBlockList();
      updateScopeParam(normalized);
    },
    [resetBlockList, updateScopeParam],
  );

  const handleSearchSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.currentTarget);
      const hash = String(formData.get("hash") ?? "").trim();
      if (hash) {
        updatePath({ hash });
      }
    },
    [updatePath],
  );

  const getPubKey = useCallback(
    async (who: string | null) => {
      if (!who || !nodeURL) {
        return null;
      }
      if (publicKeysRef.current[who]) {
        return publicKeysRef.current[who];
      }
      if (pendingKeysRef.current[who]) {
        return pendingKeysRef.current[who];
      }
      const cachedKey = getCachedPublicKey(who);
      if (cachedKey) {
        try {
          const importedKey = await window.crypto.subtle.importKey(
            "spki",
            pemToArrayBufferPublic(cachedKey),
            {
              name: "RSA-PSS",
              hash: { name: "SHA-256" },
            },
            false,
            ["verify"],
          );
          publicKeysRef.current[who] = importedKey;
          return importedKey;
        } catch (error) {
          clearCachedPublicKey(who);
        }
      }
      const requestPromise = (async () => {
        try {
          const response = await fetch(`${nodeURL}/_keys/${who}.pub.key`);
          const rawKey: unknown = await response.json();
          if (typeof rawKey !== "string" || !rawKey) {
            return null;
          }
          setCachedPublicKey(who, rawKey);
          const importedKey = await window.crypto.subtle.importKey(
            "spki",
            pemToArrayBufferPublic(rawKey),
            {
              name: "RSA-PSS",
              hash: { name: "SHA-256" },
            },
            false,
            ["verify"],
          );
          publicKeysRef.current[who] = importedKey;
          return importedKey;
        } catch (error) {
          return null;
        } finally {
          delete pendingKeysRef.current[who];
        }
      })();
      pendingKeysRef.current[who] = requestPromise;
      return requestPromise;
    },
    [nodeURL],
  );

  const addBlock = useCallback((block: Block) => {
    const heightKey = Number(block.height);
    if (!Number.isNaN(heightKey)) {
      heightToBlockRef.current[heightKey] = block;
    }
    if (blocksPerHashRef.current[block.hash]) {
      blocksPerHashRef.current[block.hash] = block;
      return;
    }
    blocksPerHashRef.current[block.hash] = block;
    setBlocks((prev) => {
      const next = [...prev, block];
      next.sort((a, b) => {
        const heightDiff = Number(b.height) - Number(a.height);
        if (heightDiff !== 0) {
          return heightDiff;
        }
        return Number(b.timestamp) - Number(a.timestamp);
      });
      return next;
    });
  }, []);

  const markBlockFresh = useCallback((hash: string) => {
    if (typeof window === "undefined") {
      return;
    }
    setFreshBlocks((prev) => {
      if (prev.has(hash)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(hash);
      return next;
    });
  }, []);

  const clearFreshBlock = useCallback((hash: string) => {
    setFreshBlocks((prev) => {
      if (!prev.has(hash)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(hash);
      return next;
    });
  }, []);

  const enhanceBlock = useCallback(
    async (block: Block, scopeKey: CryptoKey | null) => {
      const enhanced = { ...block } as Block;
      enhanced.size = JSON.stringify(block).length;
      const timestamp = Number(block.timestamp);
      enhanced.prettyDate = Number.isFinite(timestamp)
        ? new Date(timestamp).toISOString().replace("T", " ")
        : "";
      enhanced.body = block.data || "";

      const description = (block as Block & { description?: string }).description;
      if (description) {
        enhanced.htmlDescription = description;
      }

      const algorithmParameters = {
        name: "RSA-PSS",
        saltLength: 32,
      };

      const signaturePayload = {
        prevHash: block.prevHash,
        height: block.height,
        version: block.version,
        data: block.data,
        timestamp: block.timestamp,
        scope: block.scope,
        by: block.by,
      };

      const signerKey =
        block.by && block.scope && block.by !== block.scope ? await getPubKey(block.by) : scopeKey;

      if (signerKey) {
        try {
          enhanced.signature_verified = await window.crypto.subtle.verify(
            algorithmParameters,
            signerKey,
            base64ToArrayBuffer(block.signature),
            new TextEncoder().encode(JSON.stringify(signaturePayload, null, 2)),
          );
        } catch (error) {
          enhanced.signature_verified = false;
        }
      } else {
        enhanced.signature_verified = false;
      }

      const hashPayload = {
        prevHash: block.prevHash,
        height: block.height,
        version: block.version,
        data: block.data,
        timestamp: block.timestamp,
        scope: block.scope,
        by: block.by,
        signature: block.signature,
      };

      try {
        const digest = await window.crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(JSON.stringify(hashPayload)),
        );
        enhanced.hash_verified = buf2hex(digest) === block.hash;
      } catch (error) {
        enhanced.hash_verified = false;
      }

      if (block.data && block.data.includes("::")) {
        try {
          const decrypted = await decryptBlockData(block.data);
          enhanced.encripted = decrypted.encrypted;
          enhanced.formattedDecryptedData = decrypted.formatted;
        } catch (error) {
          enhanced.encripted = false;
        }
      } else {
        enhanced.encripted = false;
      }

      return enhanced;
    },
    [getPubKey],
  );

  const getBlock = useCallback(
    async (scope: string, height: number) => {
      const response = await fetch(`${nodeURL}/_height/${scope}/${height}`);
      return (await response.json()) as Block;
    },
    [nodeURL],
  );

  const getHead = useCallback(
    async (scope: string) => {
      const response = await fetch(`${nodeURL}/_head/${scope}`);
      return response.json() as Promise<HeadResponse>;
    },
    [nodeURL],
  );

  const getAndProcessBlock = useCallback(
    async (scope: string, height: number, scopeKeyOverride?: CryptoKey | null) => {
      const block = (await getBlock(scope, height)) as Block;
      if (!block || !block.hash) {
        return;
      }
      const processed = await enhanceBlock(block, scopeKeyOverride ?? scopeKeyRef.current);
      addBlock(processed);
      return processed;
    },
    [addBlock, enhanceBlock, getBlock],
  );

  const enqueueBlockLoad = useCallback(
    (scope: string, height: number, scopeKey?: CryptoKey | null) => {
      if (heightToBlockRef.current[height]) {
        return Promise.resolve(heightToBlockRef.current[height]);
      }
      if (blockFetchPromisesRef.current[height]) {
        return blockFetchPromisesRef.current[height];
      }
      const promise = (async () => {
        try {
          return await getAndProcessBlock(scope, height, scopeKey);
        } finally {
          delete blockFetchPromisesRef.current[height];
        }
      })();
      blockFetchPromisesRef.current[height] = promise;
      return promise;
    },
    [getAndProcessBlock],
  );

  const loadBlocksRange = useCallback(
    async (scope: string, startHeight: number, endHeight: number, scopeKey?: CryptoKey | null) => {
      if (startHeight < endHeight) {
        return;
      }
      const heightsToFetch: number[] = [];
      for (let height = startHeight; height >= endHeight; height -= 1) {
        if (height < 0) {
          break;
        }
        if (!heightToBlockRef.current[height]) {
          heightsToFetch.push(height);
        }
      }
      if (!heightsToFetch.length) {
        return;
      }
      await Promise.all(heightsToFetch.map((height) => enqueueBlockLoad(scope, height, scopeKey)));
    },
    [enqueueBlockLoad],
  );

  const handleLoadMore = useCallback(async () => {
    if (isUnknownScope || isLoadingBlocks || !hasMoreBlocks) {
      return;
    }
    const scope = activeScope || "root";
    const startHeight = nextHeightToLoadRef.current;
    if (startHeight === null || startHeight < 0) {
      setHasMoreBlocks(false);
      return;
    }

    setIsLoadingBlocks(true);
    try {
      const scopeKey = scopeKeyRef.current ?? (await getPubKey(scope));
      if (scopeKey) {
        scopeKeyRef.current = scopeKey;
      }
      const endHeight = Math.max(startHeight - blocksPerPage + 1, 0);
      await loadBlocksRange(scope, startHeight, endHeight, scopeKey);
      nextHeightToLoadRef.current = endHeight - 1;
      setHasMoreBlocks(nextHeightToLoadRef.current >= 0);
      setPagesLoaded((prev) => prev + 1);
    } finally {
      setIsLoadingBlocks(false);
    }
  }, [activeScope, blocksPerPage, getPubKey, hasMoreBlocks, isLoadingBlocks, isUnknownScope, loadBlocksRange]);

  const loadLatestChunk = useCallback(async () => {
    if (isUnknownScope || isLoadingLatest || latestBlockHeight === null) {
      return;
    }
    const scope = activeScope || "root";
    setIsLoadingLatest(true);
    try {
      const scopeKey = scopeKeyRef.current ?? (await getPubKey(scope));
      if (scopeKey) {
        scopeKeyRef.current = scopeKey;
      }
      const startHeight = latestBlockHeight;
      const endHeight = Math.max(startHeight - blocksPerPage + 1, 0);
      await loadBlocksRange(scope, startHeight, endHeight, scopeKey);
    } finally {
      setIsLoadingLatest(false);
    }
  }, [activeScope, blocksPerPage, getPubKey, latestBlockHeight, loadBlocksRange, isLoadingLatest, isUnknownScope]);

  useEffect(() => {
    if (!nodeURL || isBlockView || isUnknownScope) {
      return;
    }

    let cancelled = false;

    async function syncNow() {
      const currentScope = activeScope || "root";
      setSyncStatus("Syncing...");
      setIsLoadingBlocks(true);

      try {
        const rootHead = await getHead(currentScope);
        if (cancelled) {
          return;
        }

        if (rootHead.progress) {
          setSyncStatus(`Syncing (${rootHead.progress})`);
          return;
        }

        setSyncStatus(null);
        setLastBlockHash(rootHead.head);
        setLatestBlockHeight(rootHead.height);
        scopeKeyRef.current = await getPubKey(currentScope);

        if (currentLastBlockHash.current !== rootHead.head) {
          const startHeight = rootHead.height;
          const endHeight = Math.max(startHeight - blocksPerPage + 1, 0);
          nextHeightToLoadRef.current = endHeight - 1;
          setHasMoreBlocks(nextHeightToLoadRef.current >= 0);
          currentLastBlockHash.current = rootHead.head;
          await loadBlocksRange(currentScope, startHeight, endHeight, scopeKeyRef.current);
          setLastUpdate(new Date().toLocaleString());
        }
      } finally {
        if (!cancelled) {
          setIsLoadingBlocks(false);
        }
      }
    }

    void syncNow();

    return () => {
      cancelled = true;
    };
  }, [activeScope, blocksPerPage, getHead, getPubKey, isBlockView, isUnknownScope, loadBlocksRange, nodeURL]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let isActive = true;
    let reconnectTimer: number | null = null;

    const connect = () => {
      if (!isActive) {
        return;
      }

      const websocket = new WebSocket(getWebSocketURL());
      websocketRef.current = websocket;

      websocket.onopen = () => {
        websocketReconnectAttemptsRef.current = 0;
        const clientId = `explorer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        websocket.send(
          JSON.stringify({
            clientId,
            action: "subscribe",
            topic: WEBSOCKET_SUBSCRIPTION,
          }),
        );
      };

      websocket.onmessage = (event) => {
        if (!isActive) {
          return;
        }

        if (typeof event.data !== "string") {
          return;
        }

        let payload: WebSocketPayload | null = null;
        try {
          payload = JSON.parse(event.data) as WebSocketPayload;
        } catch (error) {
          return;
        }

        if (!payload || typeof payload !== "object") {
          return;
        }

        if (payload.type) {
          return;
        }

        if (!payload.topic || typeof payload.topic !== "string") {
          return;
        }

        if (!payload.topic.startsWith(WEBSOCKET_TOPIC_PREFIX)) {
          return;
        }

        const topicScope = payload.topic.slice(WEBSOCKET_TOPIC_PREFIX.length).trim();
        const block = payload.value;
        if (!block || typeof block !== "object" || !block.hash) {
          return;
        }

        const resolvedScope =
          typeof block.scope === "string" && block.scope.trim().length > 0
            ? block.scope
            : topicScope;
        if (!resolvedScope) {
          return;
        }

        const currentScope = selectedScopeRef.current;
        if (currentScope && resolvedScope.toLowerCase() !== currentScope.toLowerCase()) {
          return;
        }

        if (isBlockViewRef.current && !followHeadRef.current) {
          return;
        }

        void (async () => {
          const scopeKey = scopeKeyRef.current ?? (await getPubKey(resolvedScope));
          if (!scopeKeyRef.current && scopeKey) {
            scopeKeyRef.current = scopeKey;
          }
          const processed = await enhanceBlock(block, scopeKey ?? null);
          const isNewBlock = !blocksPerHashRef.current[processed.hash];
          addBlock(processed);
          if (isNewBlock) {
            markBlockFresh(processed.hash);
          }
          setLastUpdate(new Date().toLocaleString());
          setLastBlockHash(block.hash);
          setLatestBlockHeight(Number(block.height));
          currentLastBlockHash.current = block.hash;
        })();
      };

      websocket.onerror = () => {
        if (!isActive) {
          return;
        }
        websocket.close();
      };

      websocket.onclose = () => {
        websocketRef.current = null;
        if (!isActive) {
          return;
        }

        const nextAttempt = websocketReconnectAttemptsRef.current + 1;
        websocketReconnectAttemptsRef.current = nextAttempt;
        const delay = Math.min(
          WEBSOCKET_RECONNECT_BASE_MS * 2 ** Math.max(0, nextAttempt - 1),
          WEBSOCKET_RECONNECT_MAX_MS,
        );
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          connect();
        }, delay);
      };
    };

    connect();

    return () => {
      isActive = false;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      if (websocketRef.current) {
        websocketRef.current.onopen = null;
        websocketRef.current.onmessage = null;
        websocketRef.current.onerror = null;
        websocketRef.current.onclose = null;
        websocketRef.current.close();
        websocketRef.current = null;
      }
    };
  }, [addBlock, enhanceBlock, getPubKey]);

  useEffect(() => {
    if (!nodeURL || !hashParam) {
      setBlockView(null);
      setBlockViewError(null);
      return;
    }

    let cancelled = false;

    async function fetchBlockView() {
      try {
        const response = await fetch(`${nodeURL}/_block/${hashParam}`);
        const block = (await response.json()) as Block;
        if (cancelled) {
          return;
        }

        if (!block || !block.hash) {
          setBlockViewError("Requested block not found.");
          return;
        }
        const scopeKey = await getPubKey(block.scope);
        const processed = await enhanceBlock(block, scopeKey);
        if (!cancelled) {
          const resolvedScope = ((processed.scope || "root").trim() || "root");
          setBlockView(processed);
          setBlockViewError(null);
          setSelectedScope(resolvedScope);
          setScopeInput(resolvedScope);
        }
      } catch (error) {
        if (!cancelled) {
          setBlockViewError("Could not load the requested block.");
        }
      }
    }

    void fetchBlockView();

    return () => {
      cancelled = true;
    };
  }, [enhanceBlock, getPubKey, hashParam, nodeURL]);

  useEffect(() => {
    if (!nodeURL || !blockView || !isBlockView) {
      return;
    }
    const activeBlockView = blockView;

    let cancelled = false;

    async function loadBlockNeighborhood() {
      setIsLoadingBlocks(true);
      try {
        const scope = activeBlockView.scope || "root";
        const head = await getHead(scope);
        if (cancelled) {
          return;
        }
        if (head.progress) {
          return;
        }

        setLastBlockHash(head.head);
        setLatestBlockHeight(head.height);
        const scopeKey = await getPubKey(scope);
        if (scopeKey) {
          scopeKeyRef.current = scopeKey;
        }

        const maxHeight = Math.min(head.height, activeBlockView.height + BLOCKS_AROUND_COUNT);
        const minHeight = Math.max(0, activeBlockView.height - BLOCKS_AROUND_COUNT);
        await loadBlocksRange(scope, maxHeight, minHeight, scopeKey);
        if (cancelled) {
          return;
        }

        nextHeightToLoadRef.current = minHeight - 1;
        setHasMoreBlocks(nextHeightToLoadRef.current >= 0);
        setPagesLoaded(1);
        setFollowHead(activeBlockView.height + BLOCKS_AROUND_COUNT >= head.height);
      } finally {
        if (!cancelled) {
          setIsLoadingBlocks(false);
        }
      }
    }

    void loadBlockNeighborhood();

    return () => {
      cancelled = true;
    };
  }, [blockView, getHead, getPubKey, isBlockView, loadBlocksRange, nodeURL]);

  useEffect(() => {
    if (!blockView) {
      return;
    }
    const target = blockDetailsRef.current;
    if (!target) {
      return;
    }
    window.requestAnimationFrame(() => {
      const banner = document.querySelector(".block-view-banner") as HTMLElement | null;
      const offset = banner ? banner.offsetHeight + 16 : 24;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    });
  }, [blockView]);

  const handleHashClick = useCallback(
    (hash: string) => {
      if (typeof window !== "undefined") {
        let embedded = false;
        try {
          embedded = window.self !== window.top;
        } catch (error) {
          embedded = true;
        }
        if (embedded) {
          const url = new URL(window.location.origin);
          url.pathname = `${BASE_PATH}/block/hash/${encodeURIComponent(hash)}`;
          window.open(url.toString(), "_blank", "noopener,noreferrer");
          return;
        }
      }
      updatePath({ hash });
    },
    [updatePath],
  );

  const handleOpenScopeChain = useCallback(
    (scopeHash: string) => {
      applyScope(scopeHash);
    },
    [applyScope],
  );


  const handleExitBlockView = useCallback(() => {
    setBlockView(null);
    setBlockViewError(null);
    updatePath({ hash: null });
    window.scrollTo(0, 0);
  }, [updatePath]);

  const renderInfoCards = () => (
    <div className="info-grid">
      {/* <div className="panel border-gradient">
        <p className="input-label">Consensus</p>
        <h2 className="panel-title">Proof of authority 🔐</h2>
        <p className="text-muted">
          "PoA" is a blockchain consensus algorithm that enables fast transactions using digital
          signatures.
        </p>
      </div>
      <div className="panel border-gradient">
        <p className="input-label">Integrity</p>
        <h2 className="panel-title">Verification active ✅</h2>
        <p className="text-muted">
          This explorer validates each block in real time, checking the digital signature and the
          unique hash that proves immutability.
        </p>
      </div> */}
    </div>
  );

  const scopeLink = blockView?.scope ?? selectedScope;
  const scopeLabel = blockView?.scope || selectedScope || "root";
  const resolvedSyncStatus = isUnknownScope ? "Unknown scope" : syncStatus;
  const syncLabel = isBlockView ? "Block view" : resolvedSyncStatus ?? "Synced";
  const statusClassName = isBlockView
    ? "status-badge status-badge--muted"
    : resolvedSyncStatus
      ? "status-badge status-badge--alert"
      : "status-badge status-badge--ok";
  const lastUpdateLabel = isBlockView ? "-" : lastUpdate;
  const networkLabel = nodeURL.includes("testnet") ? "testnet" : "custom";
  const selectedHash = blockView?.hash ?? hashParam ?? "";
  const blockIndex = blockView ? blocks.findIndex((item) => item.hash === blockView.hash) : -1;
  const nextBlockHash =
    blockIndex > 0 ? blocks[blockIndex - 1]?.hash : undefined;
  const previousBlockHash =
    blockIndex >= 0 && blockIndex < blocks.length - 1 ? blocks[blockIndex + 1]?.hash : undefined;
  const minVisibleBlocks = isBlockView ? BLOCKS_AROUND_TOTAL : 0;
  const visibleBlockCount = Math.max(blocksPerPage * pagesLoaded, minVisibleBlocks);
  const effectiveBlocks = isUnknownScope ? [] : blocks;
  const visibleBlocks = effectiveBlocks.slice(0, visibleBlockCount);
  const topVisibleHeight = visibleBlocks.length ? Number(visibleBlocks[0].height) : null;
  const showTopLoadMoreButton =
    !isUnknownScope &&
    latestBlockHeight !== null &&
    (topVisibleHeight === null || topVisibleHeight < latestBlockHeight);
  const canLoadMore = !isUnknownScope && hasMoreBlocks;

  const isEmbedded = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      return window.self !== window.top;
    } catch (error) {
      return true;
    }
  }, []);

  const renderBlockList = (embedded = false) => (
    <section className={embedded ? "block-list block-list--embed" : "panel border-gradient block-list"}>
      {!embedded ? (
        <>
          <div className="panel-header block-list-header">
            <div>
              <p className="input-label">Blocks</p>
              <h2 className="panel-title panel-title--scoped">
                <span>Scope:</span>
                <span className="panel-title-scope">
                  <HashAwareText value={scopeLabel} useMiddleEllipsis />
                </span>
              </h2>
            </div>
          </div>
          {showTopLoadMoreButton ? (
            <div className="block-list-top-load">
              <button
                type="button"
                className="button-secondary"
                onClick={loadLatestChunk}
                disabled={isLoadingBlocks || isLoadingLatest}
              >
                {isLoadingLatest ? "Loading..." : `Load latest (${blocksPerPage})`}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      <div className="table-wrapper">
        {visibleBlocks.length === 0 ? (
          <div className="block-list-empty">No blocks in this scope yet.</div>
        ) : (
          <table className="blocks-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Hash</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody className="mono-font">
              {visibleBlocks.map((block) => {
                const hashLower = block.hash.toLowerCase();
                const hasSubChain = scopeSet.has(hashLower);
                const blockHeight = Number(block.height);
                const isLatestBlock =
                  latestBlockHeight !== null && blockHeight === latestBlockHeight;
                const hasSmartContract = block.data?.trimStart().startsWith("//");
                const hasError = Boolean(block.__err);
                const errorMessage =
                  typeof block.__err === "string"
                    ? block.__err
                    : typeof block.error === "string"
                      ? block.error
                      : "";
                const rawBlockUrl = `https://testnet.democrachain.org/v1/_block/${block.hash}`;
                return (
                  <tr
                    key={block.hash}
                    onClick={() => {
                      handleHashClick(block.hash);
                    }}
                    className={`clickable${block.hash === selectedHash ? " is-selected" : ""}${freshBlocks.has(block.hash) ? " is-new" : ""}`}
                    onAnimationEnd={(event) => {
                      if (event.animationName === "new-block-row") {
                        clearFreshBlock(block.hash);
                      }
                    }}
                  >
                    <th scope="row">
                      <button
                        type="button"
                        className="table-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleHashClick(block.hash);
                        }}
                      >
                        {block.height}
                      </button>
                    </th>
                    <td>
                      <div className="hash-cell">
                        <button
                          type="button"
                          className="table-link mono-font hash-link"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleHashClick(block.hash);
                          }}
                        >
                          <HashShort
                            value={block.hash}
                            className={hasSubChain ? "hash-highlight" : ""}
                            maxLength={10}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="content-cell">
                      <span className="content-date">
                        <span className="content-date-text">{block.prettyDate}</span>
                        <span className="by-name" style={{ marginLeft: "auto" }}>{block.by}</span>
                        {isLatestBlock ? (
                          <span className="verification-chip is-last inline-chip" title="Latest block in this scope">
                            SCOPE HEAD
                          </span>
                        ) : null}
                        <span className="content-date-chips">
                          {hasError ? (
                            <span
                              className="verification-chip is-error"
                              title={`There was an error: ${errorMessage || "Unknown"}`}
                            >
                              Err
                            </span>
                          ) : hasSmartContract ? (
                            <span
                              className="verification-chip is-done"
                              title="Smart contract completed."
                            >
                              GOOD
                            </span>
                          ) : (
                            <span className="verification-chip is-txt" title="Only-text block">
                              TXT
                            </span>
                          )}
                        </span>
                      </span>

                      <div className="block-preview">{getPreviewText(block.data)}</div>
                      {hasSubChain ? (
                        <div className="content-date-chips hash-actions">
                          <button
                            type="button"
                            className="button-secondary button-compact block-subchain-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenScopeChain(block.hash);
                            }}
                          >
                            View blocks using this contract
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!embedded ? (
        <div className="block-list-footer">
          <span className="block-list-count">Showing {visibleBlocks.length} blocks</span>
          <button
            type="button"
            className="button-secondary"
            onClick={handleLoadMore}
            disabled={isLoadingBlocks || !canLoadMore}
          >
            {isLoadingBlocks
              ? "Loading..."
              : canLoadMore
                ? `Load more (${blocksPerPage})`
                : "No more blocks"}
          </button>
        </div>
      ) : null}
    </section>
  );

  if (isEmbedded) {
    return <div className="embed-shell">{renderBlockList(true)}</div>;
  }

  return (
    <div className="app-shell">
      <div className="decor-layer" aria-hidden="true">
        <div className="decor-orb decor-orb--emerald" />
        <div className="decor-orb decor-orb--gold" />
        <div className="decor-square decor-square--emerald animate-float" />
        <div className="decor-dot decor-dot--gold animate-pulse-glow" />
      </div>

      <header className="site-header">
        <div className="app-container header-inner">
          <a
            className="brand"
            href="https://democrachain.org/"
            target="_blank"
            rel="noreferrer"
          >
            <Logo />
            <div className="brand-text">
              <div className="brand-title">Democrachain</div>
              <div className="brand-subtitle">Explorer</div>
            </div>
          </a>

          <div className="header-actions">
            <form id="searchForm" action="" method="get" className="search-form" role="search" onSubmit={handleSearchSubmit}>
              <input
                name="hash"
                className="input-field search-input"
                type="search"
                placeholder="Block hash"
                aria-label="Search"
                defaultValue={hashParam ?? ""}
              />
              <button className="button-secondary" type="submit">
                Search
              </button>
            </form>

            <div className="header-links">
              <a className="button-secondary" target="_blank" rel="noreferrer" href={playgroundUrl}>
                Playground
              </a>
              <a
                className="button-ghost"
                target="_blank"
                rel="noreferrer"
                href="https://democrachain.org/docs"
              >
                Docs
              </a>
            </div>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="app-container hero-grid">
          <div className="hero-copy animate-slide-up">
            <div className="hero-badge">Testnet Explorer</div>
            <h1 className="hero-title">Track every block in real time.</h1>
            <p className="text-muted hero-description">
              Explore network activity, validate signatures, and review testnet status with a
              clear experience that stays consistent across the ecosystem.
            </p>
          </div>
          <div className="panel border-gradient hero-panel animate-slide-up">
            <div className="panel-header">
              <div>
                <p className="input-label">Network</p>
                <h2 className="panel-title">Public Testnet Network</h2>
                <a className="" href="https://testnet.democrachain.org" target="_blank" rel="noreferrer"><p className="mono-font clickable">https://testnet.democrachain.org</p></a>
              </div>
              <div className="panel-meta">v1 {networkLabel}</div>
            </div>
            {isBlockView ? (
              <p className="panel-note text-muted">
                Viewing a single block. Return to the explorer list for live network status.
              </p>
            ) : (
              <div className="status-grid">
                <div className="status-item">
                  <span className="status-label">Sync status</span>
                  <span className={statusClassName}>{syncLabel}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <main className="app-container main-content">
        <section className="panel border-gradient scope-panel">
          <div className="panel-header">
            <div>
              <p className="input-label">Scope</p>
              <h2 className="panel-title">Choose a scope</h2>
            </div>
          </div>
          <div className="scope-select">
            {scopesError ? <div className="input-hint">{scopesError}</div> : null}
            <div className="scope-list">
              {availableScopes.length === 0 ? (
                <span className="scope-empty">No scopes available.</span>
              ) : (
                availableScopes.map((scope) => (
                  <button
                    key={scope}
                    type="button"
                    title={scope}
                    className={`scope-pill ${scope === selectedScope ? "is-active" : ""}`}
                    onClick={() => {
                      setScopeInput(scope);
                      applyScope(scope);
                    }}
                  >
                    <ScopeLabel value={scope} />
                  </button>
                ))
              )}
            </div>
          </div>
        </section>

        {isBlockView ? (
          <>
            <div className="panel border-gradient block-view-banner">
              <div>
                <p className="input-label">Explorer</p>
                <h2 className="panel-title">Viewing a single block</h2>
                <p className="text-muted">
                  Return to the full block list to continue exploring the network.
                </p>
              </div>
              <button type="button" className="button-primary" onClick={handleExitBlockView}>
                Back to explorer
              </button>
            </div>
            {blockViewError ? (
              <div className="alert-panel alert-panel--danger" role="alert">
                {blockViewError}
              </div>
            ) : null}
            {blockView ? (
              <div ref={blockDetailsRef}>
                <BlockDetails
                  block={blockView}
                  nodeURL={nodeURL}
                  isScopeHash={scopeSet.has(blockView.hash.toLowerCase())}
                  onOpenScopeChain={handleOpenScopeChain}
                  showClose
                  onClose={handleExitBlockView}
                  onHashClick={handleHashClick}
                  nextBlockHash={nextBlockHash}
                  previousBlockHash={previousBlockHash}
                />
              </div>
            ) : null}
            {renderBlockList()}
            {renderInfoCards()}
          </>
        ) : (
          <>
            {renderBlockList()}
            {renderInfoCards()}
          </>
        )}
      </main>

      <footer className="site-footer">
        <div className="app-container footer-inner">
          <div className="footer-brand">
            <span className="footer-title">Democrachain Explorer</span>
            <span className="footer-meta">© 2025 Democrachain</span>
          </div>
          <div className="footer-copy">Testnet monitoring and real-time verification.</div>
        </div>
      </footer>
    </div>
  );
}
